import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUp, Send, Share2, Linkedin, Facebook, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { EVENT, API_URL } from "@/data/content";

const EXPLORE = [
    { label: "About", to: "/about" },
    { label: "Experience", to: "/experience" },
    { label: "Programme", to: "/programme" },
    { label: "Exhibition", to: "/exhibition" },
    { label: "Event Map", to: "/venue#map" },
];
const ENGAGE = [
    { label: "PitchYatra", to: "/pitchyatra" },
    { label: "Investor Adda", to: "/investor-adda" },
    { label: "Knowledge Labs", to: "/knowledge-labs" },
    { label: "Awards", to: "/awards" },
    { label: "Special Zones", to: "/special-zones" },
];
const PARTICIPATE = [
    { label: "Register", to: "/register" },
    { label: "Partner", to: "/partner" },
    { label: "Venue", to: "/venue" },
    { label: "Insights", to: "/insights" },
    { label: "FAQ", to: "/faq" },
    { label: "Contact", to: "/contact" },
];

const Col = ({ head, items }) => (
    <div>
        <p className="text-[0.58rem] tracking-[0.36em] uppercase text-[var(--gold)] mb-5">{head}</p>
        <ul className="space-y-3">
            {items.map((i) => (
                <li key={i.label}>
                    <Link to={i.to} className="text-sm text-[var(--ivory)]/65 hover:text-[var(--champagne)] transition-colors">
                        {i.label}
                    </Link>
                </li>
            ))}
        </ul>
    </div>
);

export default function Footer() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const shareUrl = typeof window !== "undefined" ? window.location.origin : EVENT.website;
    const shareText = encodeURIComponent("Startup MahaBazaar 2027 — India's Grand Festival of Startups, Innovation & Entrepreneurship · 29–31 Jan, New Delhi");

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setBusy(true);
        try {
            const res = await axios.post(`${API_URL}/newsletter`, { email });
            toast.success(res.data.message);
            setEmail("");
        } catch {
            toast.error("Could not subscribe right now. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <footer className="relative border-t hairline bg-[#120308] overflow-hidden" data-testid="site-footer">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[24rem] rounded-full opacity-20"
                    style={{ background: "radial-gradient(ellipse at center, rgba(74,14,24,0.9), transparent 65%)" }} />
            </div>
            <div className="container-x relative pt-20 pb-28 md:pb-16">
                <div className="text-center">
                    <img src="/emblem.png" alt="Startup MahaBazaar emblem" className="mx-auto w-12 mb-8 opacity-90" />
                    <p className="font-display font-semibold leading-none">
                        <span className="block text-[0.62rem] tracking-[0.7em] text-[var(--gold)] font-sans font-medium">STARTUP</span>
                        <span className="block gold-text text-[11vw] md:text-[7.5rem] tracking-wide mt-2">MAHABAZAAR</span>
                        <span className="block text-lg md:text-2xl tracking-[0.55em] text-[var(--ivory)] mt-4 font-sans font-light">2027</span>
                    </p>
                    <p className="font-display italic text-[var(--champagne)]/85 mt-6 text-base md:text-lg">
                        Bazaar of Ideas, Innovation &amp; Impact
                    </p>
                    <p className="text-[0.62rem] tracking-[0.34em] uppercase text-muted-luxe mt-4">
                        29–31 January 2027 · MSIT &amp; MSI, New Delhi
                    </p>
                </div>

                <div className="divider-gold my-14" />

                <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
                    <Col head="Explore" items={EXPLORE} />
                    <Col head="Engage" items={ENGAGE} />
                    <Col head="Participate" items={PARTICIPATE} />
                    <div>
                        <p className="text-[0.58rem] tracking-[0.36em] uppercase text-[var(--gold)] mb-5">Stay in the Bazaar</p>
                        <p className="text-sm text-[var(--ivory)]/65 leading-relaxed">
                            Announcements, programme reveals and PitchYatra updates — straight to your inbox.
                        </p>
                        <form onSubmit={subscribe} className="mt-5 flex" data-testid="newsletter-form">
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Your email"
                                data-testid="newsletter-email-input"
                                className="field !py-3 text-sm flex-1 min-w-0"
                            />
                            <button type="submit" disabled={busy} data-testid="newsletter-submit-btn" aria-label="Subscribe"
                                className="btn btn-gold !px-4 !py-3 shrink-0">
                                <Send size={13} />
                            </button>
                        </form>
                        <p className="mt-6 text-[0.58rem] tracking-[0.3em] uppercase text-muted-luxe">Enquiries</p>
                        <a href={`tel:${EVENT.phone.replace(/\s/g, "")}`} className="block mt-2 text-sm text-[var(--champagne)] hover:text-[var(--gold)] transition-colors" data-testid="footer-phone">
                            {EVENT.phone}
                        </a>
                        <p className="mt-2 text-xs text-[var(--ivory)]/55">An initiative by {EVENT.organiser}</p>
                    </div>
                </div>

                <div className="divider-gold my-12" />

                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-4 text-[0.6rem] tracking-[0.28em] uppercase text-muted-luxe">
                        <span className="flex items-center gap-2"><Share2 size={12} /> Share</span>
                        <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="share-x" className="hover:text-[var(--champagne)] transition-colors">X</a>
                        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="share-linkedin" aria-label="Share on LinkedIn" className="hover:text-[var(--champagne)] transition-colors"><Linkedin size={13} /></a>
                        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="share-facebook" aria-label="Share on Facebook" className="hover:text-[var(--champagne)] transition-colors"><Facebook size={13} /></a>
                        <a href={`https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" aria-label="Share on WhatsApp" className="hover:text-[var(--champagne)] transition-colors"><MessageCircle size={13} /></a>
                    </div>
                    <div className="flex items-center gap-6 text-[0.6rem] tracking-[0.28em] uppercase text-muted-luxe">
                        <Link to="/privacy" className="hover:text-[var(--champagne)] transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
                        <Link to="/terms" className="hover:text-[var(--champagne)] transition-colors" data-testid="footer-terms">Terms &amp; Conditions</Link>
                        <Link to="/cookies" className="hover:text-[var(--champagne)] transition-colors" data-testid="footer-cookies">Cookie Policy</Link>
                    </div>
                    <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="back-to-top-btn" aria-label="Back to top"
                        className="w-10 h-10 border hairline flex items-center justify-center text-[var(--gold)] hover:border-[var(--line)] transition-colors">
                        <ArrowUp size={15} strokeWidth={1.5} />
                    </button>
                </div>
                <p className="mt-10 text-center text-[0.6rem] tracking-[0.24em] uppercase text-[var(--muted)]/60">
                    © 2027 Startup MahaBazaar · Bazaar of Ideas, Innovation &amp; Impact
                </p>
            </div>
        </footer>
    );
}
